import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, HeartHandshake } from 'lucide-react';

/** Verified parent/guardian interest, one row per school. Interest is demand, not an application. */
export default function SchoolInterestTab({ onCountChange }) {
  const [groups, setGroups] = useState([]);
  const [totals, setTotals] = useState({ new_count: 0, total_count: 0 });
  const [open, setOpen] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get('/admin/school-interest');
      setGroups(res.data.groups || []);
      setTotals({ new_count: res.data.new_count || 0, total_count: res.data.total_count || 0 });
      onCountChange?.(res.data.new_count || 0);
    } catch { /* admin-only endpoint */ }
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (g) => {
    const next = open === g.group_key ? '' : g.group_key;
    setOpen(next);
    if (next && g.new_count > 0) {
      try {
        await api.post('/admin/school-interest/seen', { group_key: g.group_key });
        load();
      } catch { /* leave the count as it is */ }
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="school-interest-tab">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-[#1F2937] font-['Outfit']">School Interest</h2>
          <p className="text-sm text-[#6B7280] mt-0.5">
            Parents and guardians asking for CareersPOV at their child's school. This is interest, not a
            school application — nothing here creates a school or an account.
          </p>
        </div>
        <Badge className="bg-[#FFF0ED] text-[#F97D6A] border-0 shrink-0" data-testid="school-interest-total">
          {totals.total_count} interest
        </Badge>
      </div>

      {groups.length === 0 ? (
        <div className="px-6 py-16 text-center" data-testid="school-interest-empty">
          <HeartHandshake className="h-7 w-7 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-[#6B7280]">No verified interest yet.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F6F8] border-b border-gray-100">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF]">School</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF]">City</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF]">Country</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">Families</th>
                <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF]">Latest</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {groups.map((g) => (
                <React.Fragment key={g.group_key}>
                  <tr onClick={() => toggle(g)} className="cursor-pointer hover:bg-[#FFF8F6] transition-colors"
                      data-testid={`school-interest-row-${g.group_key}`}>
                    <td className="py-3 px-4 font-medium text-[#1F2937]">
                      <span className="inline-flex items-center gap-1.5">
                        {open === g.group_key
                          ? <ChevronDown className="h-4 w-4 text-gray-400" />
                          : <ChevronRight className="h-4 w-4 text-gray-400" />}
                        {g.school_name}
                        {g.new_count > 0 && (
                          <Badge className="bg-[#F97D6A] text-white border-0 text-[10px]"
                                 data-testid={`school-interest-new-${g.group_key}`}>
                            {g.new_count} new
                          </Badge>
                        )}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-500">{g.city}</td>
                    <td className="py-3 px-4 text-gray-500">{g.country}</td>
                    <td className="py-3 px-4 font-semibold text-[#1F2937]">{g.interest_count}</td>
                    <td className="py-3 px-4 text-gray-500 whitespace-nowrap">
                      {g.latest_at ? new Date(g.latest_at).toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                  </tr>
                  {open === g.group_key && (
                    <tr className="bg-[#FAFBFC]">
                      <td colSpan={5} className="px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] mb-2">
                          Verified emails
                        </p>
                        <div className="space-y-1.5" data-testid={`school-interest-emails-${g.group_key}`}>
                          {g.families.map((f) => (
                            <div key={f.email} className="flex items-center justify-between gap-3 text-sm">
                              <span className="text-[#1F2937]">{f.email}</span>
                              <span className="text-xs text-gray-400 whitespace-nowrap">
                                {f.verified_at ? new Date(f.verified_at).toLocaleDateString('en-GB', {
                                  day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
